import _ from 'lodash';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

import { apiAxios } from '@/apiAxios';
import PresetTag from '@/classes/PresetTag';
import { IApiResponsePresetTagsList, IApiResponsePresetTagsSearch, IApiResponsePresetTagsUpdate } from '@/interfaces';
import { upsert } from '@/main';
import store from '@/store/store';

@Module({ dynamic: true, store, name: 'PresetTagsModule', namespaced: true })
export default class PresetTagsModule extends VuexModule {
  public presettags = [] as PresetTag[];
  public currentPage = 0;
  public hasMore = true;

  @Mutation
  public setHasMore(newHasMore: boolean) {
    this.hasMore = newHasMore;
  }

  @Mutation
  public increasePageIndex() {
    this.currentPage++;
  }

  @Mutation
  public upsertPresetTags(newPresetTags: PresetTag[]) {
    const results = this.presettags;
    _.each(newPresetTags, (newPresetTag) => {
      upsert<PresetTag>(results, { presettagid: newPresetTag.presettagid }, newPresetTag);
    });
    this.presettags = results;
  }

  @Mutation
  public upsertPresetTag(newPresetTag: PresetTag) {
    const results = this.presettags;
    upsert<PresetTag>(results, { presettagid: newPresetTag.presettagid }, newPresetTag);

    this.presettags = results;
  }

  @Mutation
  public prependPresetTag(newPresetTag: PresetTag) {
    const results = this.presettags;
    results.unshift(newPresetTag);

    this.presettags = results;
  }

  @Action
  public async apiLoadNextPage(arg: { pageSize: number }): Promise<IApiResponsePresetTagsList> {
    try {
      const { pageSize } = arg;

      const response = await apiAxios.get<IApiResponsePresetTagsList>(`api/presettag/page/${ this.currentPage }`, {
        params: { pagesize: pageSize },
      });
      const presettags = response.data.payload.presettags;

      if (presettags.length > 0) {
        this.upsertPresetTags(response.data.payload.presettags);
        this.increasePageIndex();

        if (presettags.length < pageSize) {
          this.setHasMore(false);
        }
      } else {
        // No item
        this.setHasMore(false);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiAddPresetTag(arg: { presetTag: PresetTag }): Promise <IApiResponsePresetTagsUpdate> {
    try {
      const { presetTag } = arg;

      const response = await apiAxios.post<IApiResponsePresetTagsUpdate>('api/presettag/create', presetTag);

      if (response.status === 200) {
        this.prependPresetTag(response.data.payload.presettag);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiUpdatePresetTag(arg: { presetTag: PresetTag }): Promise <IApiResponsePresetTagsUpdate> {
    try {
      const { presetTag } = arg;

      const response = await apiAxios.post<IApiResponsePresetTagsUpdate>('api/presettag/update', presetTag);

      this.upsertPresetTag(response.data.payload.presettag);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async apiSearchPresetTags(arg: { searchText: string }): Promise<IApiResponsePresetTagsSearch> {
    try {
      const { searchText } = arg;

      const response = await apiAxios.post<IApiResponsePresetTagsSearch>('api/presettag/search', { searchText });

      this.upsertPresetTags(response.data.payload.presettags);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}
